import { useQuery } from '@tanstack/react-query'

import { Esqueleto, TituloSecao } from '@/componentes/ui'
import { CHAVES } from '@/lib/sessao'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utilitarios'
import type { Conquista } from '@/tipos/tabelas'

const EMOJI_POR_SLUG: Record<string, string> = {
  primeira_semana: '📅',
  sete_checkins: '🔥',
  primeira_caminhada: '👣',
  primeiro_exercicio: '🏋️',
  programa_concluido: '🏆',
}

/**
 * Conquistas (seção 12) — gamificação leve.
 *
 * As bloqueadas aparecem em cinza, não escondidas: saber o que falta é
 * o que puxa a próxima ação. Esconder transformaria a seção numa lista
 * do que já passou.
 */
export function Conquistas({
  matriculaId,
  programaId,
}: {
  matriculaId: string
  programaId: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: CHAVES.conquistas(matriculaId),
    queryFn: async () => {
      const [{ data: todas }, { data: minhas }] = await Promise.all([
        supabase.from('conquistas').select('*').eq('programa_id', programaId).order('ordem'),
        supabase
          .from('conquistas_desbloqueadas')
          .select('conquista_id, desbloqueada_em')
          .eq('matricula_id', matriculaId),
      ])

      const desbloqueadas = new Map(
        ((minhas ?? []) as { conquista_id: string; desbloqueada_em: string }[]).map((linha) => [
          linha.conquista_id,
          linha.desbloqueada_em,
        ]),
      )

      return { todas: (todas ?? []) as Conquista[], desbloqueadas }
    },
  })

  if (isLoading) return <Esqueleto className="h-40" />
  if (!data || data.todas.length === 0) return null

  const total = data.desbloqueadas.size

  return (
    <section>
      <TituloSecao
        acao={
          <span className="font-titulo text-sm font-semibold text-dourado">
            {total} de {data.todas.length}
          </span>
        }
      >
        Conquistas
      </TituloSecao>

      <ul className="grid grid-cols-2 gap-3">
        {data.todas.map((conquista) => {
          const desbloqueada = data.desbloqueadas.has(conquista.id)

          return (
            <li
              key={conquista.id}
              className={cn(
                'flex flex-col items-center gap-1 rounded-2xl p-4 text-center transition-colors',
                desbloqueada
                  ? 'bg-linear-to-br from-dourado/25 to-coral/20 shadow-[var(--shadow-suave)]'
                  : 'bg-[var(--cartao)] opacity-55',
              )}
            >
              <span className={cn('text-3xl', !desbloqueada && 'grayscale')} aria-hidden>
                {EMOJI_POR_SLUG[conquista.slug] ?? '⭐'}
              </span>
              <span className="font-titulo text-[13px] leading-tight font-semibold">
                {conquista.nome}
              </span>
              <span className="text-[11px] leading-snug text-[var(--tinta-apagada)]">
                {conquista.descricao}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
